import { message } from 'antd';
import axios from 'axios';
import { StatusCodes } from 'http-status-codes';
import { validateUrlParams } from '../utils/urlValidation';
import {
  getEncryptedToken,
  setEncryptedToken,
  removeEncryptedToken,
} from './tokenStorage';

export const SESSION_KEY = 'bic_token';
export const SESSION_USERNAME = 'bic_username';
export const SESSION_EXPIRY = 'bic_expiry';
export const SSO_BASE_URL = process.env.REACT_APP_API_SSO_BASE_URL;
const LMI_PROJECT_ID = process.env.REACT_APP_PROJECT_ID;

let responseInterceptorId = null;

const setAuthorizationHeader = () => {
  const token = getEncryptedToken();
  axios.defaults.headers.common.Authorization = token
    ? `Bearer ${token}`
    : undefined;
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
  }
  responseInterceptorId = axios.interceptors.response.use(
    (res) => res,
    async (err) => {
      const status = err && err.response ? err.response.status : null;
      switch (status) {
        case StatusCodes.UNAUTHORIZED:
          message.destroy();
          message.error('Session expired. Please login again');
          window.location.replace('/');
          return Promise.reject(err);
        default:
          return Promise.reject(err);
      }
    }
  );
};

export default {
  get session() {
    return {
      token: getEncryptedToken(),
      username: localStorage.getItem(SESSION_USERNAME),
      expiry: localStorage.getItem(SESSION_EXPIRY),
    };
  },
  get isLoggedIn() {
    const expiry = localStorage.getItem(SESSION_EXPIRY);
    if (!expiry || expiry <= Date.now()) return false;
    return !!getEncryptedToken();
  },
  get callbackLoginUrl() {
    return `${window.location.origin}/login`;
  },
  get callbackLogoutUrl() {
    return `${window.location.origin}/logout`;
  },
  get ssoLoginUrl() {
    return `${SSO_BASE_URL}/?projectId=${LMI_PROJECT_ID}&callbackUrl=${this.callbackLoginUrl}`;
  },
  get ssoLogoutUrl() {
    return `${SSO_BASE_URL}/Signout?callbackUrl=${this.callbackLogoutUrl}`;
  },
  login(token, username, expiry) {
    if (!token) throw new Error('Invalid token');
    setEncryptedToken(token);
    localStorage.setItem(SESSION_USERNAME, username);
    localStorage.setItem(SESSION_EXPIRY, Date.now() + expiry * 1000);
    setAuthorizationHeader();
  },
  logout() {
    removeEncryptedToken();
    localStorage.removeItem(SESSION_USERNAME);
    localStorage.removeItem(SESSION_EXPIRY);
    delete axios.defaults.headers.common.Authorization;
    if (responseInterceptorId !== null) {
      axios.interceptors.response.eject(responseInterceptorId);
      responseInterceptorId = null;
    }
  },
  setAuthorizationHeader,
};

export const checkUrlAccess = () => {
  const url = new URL(window.location);

  // Validate and sanitize URL parameters
  const validatedParams = validateUrlParams(url);
  const { encryptedData, calcId, policyId, referenceId, stepId } =
    validatedParams;

  const isClientAccess =
    (encryptedData && calcId) ||
    (encryptedData && referenceId && stepId) ||
    (encryptedData && policyId);

  const isManualLogin =
    validatedParams.manualLogin === 'true' ||
    validatedParams.manualLogin === '1';

  return {
    url,
    encryptedData,
    policyId,
    referenceId,
    stepId,
    isClientAccess,
    isManualLogin,
  };
};
